/**
 * DataTable (U5): glass table with sticky header, optional column sorting,
 * numeric (mono/tabular) columns, clickable rows, and an empty state.
 * Presentation-only — server data, filtering, and pagination live upstream.
 */
import { useMemo, useState, type JSX, type ReactNode } from 'react';
import { cx } from './cx';

export interface DataTableColumn<Row> {
  key: string;
  header: ReactNode;
  align?: 'start' | 'end' | 'center';
  /** CSS width, e.g. '10rem'. */
  width?: string;
  /** Monospace tabular numerals (money, qty, ids). */
  mono?: boolean;
  render: (row: Row, index: number) => ReactNode;
  /** Presence enables sorting on this column. */
  sort?: (a: Row, b: Row) => number;
}

export interface DataTableProps<Row> {
  columns: ReadonlyArray<DataTableColumn<Row>>;
  rows: ReadonlyArray<Row>;
  rowKey: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  empty?: ReactNode;
  initialSort?: { key: string; dir: 'asc' | 'desc' };
  density?: 'comfortable' | 'compact';
  maxHeight?: string;
  'aria-label'?: string;
  className?: string;
  /** Optional data-testid per body row. */
  rowTestId?: (row: Row) => string;
}

export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty = 'Nothing here yet.',
  initialSort,
  density = 'comfortable',
  maxHeight,
  'aria-label': ariaLabel,
  className,
  rowTestId,
}: DataTableProps<Row>): JSX.Element {
  const [sort, setSort] = useState(initialSort ?? null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find((c) => c.key === sort.key);
    if (!column?.sort) return rows;
    const copy = [...rows].sort(column.sort);
    if (sort.dir === 'desc') copy.reverse();
    return copy;
  }, [rows, columns, sort]);

  function toggleSort(column: DataTableColumn<Row>): void {
    if (!column.sort) return;
    setSort((prev) =>
      prev?.key === column.key
        ? { key: column.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key: column.key, dir: 'asc' },
    );
  }

  return (
    <div className={cx('bl-table-wrap', className)} style={maxHeight ? { maxHeight } : undefined}>
      <table
        className={cx('bl-table', density === 'compact' && 'bl-table--compact')}
        aria-label={ariaLabel}
      >
        <thead>
          <tr>
            {columns.map((column) => {
              const alignClass =
                column.align === 'end' ? 'is-end' : column.align === 'center' ? 'is-center' : '';
              const active = sort?.key === column.key;
              return (
                <th
                  key={column.key}
                  className={alignClass}
                  style={column.width ? { width: column.width } : undefined}
                  aria-sort={active ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {column.sort ? (
                    <button
                      type="button"
                      className="bl-table__sort"
                      onClick={() => toggleSort(column)}
                    >
                      {column.header}
                      <span className="bl-table__sort-arrow" aria-hidden="true">
                        {active ? (sort?.dir === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    </button>
                  ) : (
                    <span className="bl-table__sort bl-table__sort--static">{column.header}</span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>
                <div className="bl-table__empty">{empty}</div>
              </td>
            </tr>
          ) : (
            sorted.map((row, index) => (
              <tr
                key={rowKey(row)}
                data-testid={rowTestId?.(row)}
                className={cx(onRowClick && 'is-clickable')}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={
                  onRowClick
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cx(
                      column.align === 'end' && 'is-end',
                      column.align === 'center' && 'is-center',
                      column.mono && 'is-mono',
                    )}
                  >
                    {column.render(row, index)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
